import {
  CanDeactivateFn,
  Router,
  Routes,
  UrlMatchResult,
  UrlSegment,
} from '@angular/router';
import { inject } from '@angular/core';
import {
  authGuard,
  securityAdministratorGuard,
  noAccessGuard,
} from '@core/guards/auth.guard';
import { NoAccessComponent } from 'ngx-mt-ui-lib/components/no-access';
import { PageNotFoundComponent } from 'ngx-mt-ui-lib/components/page-not-found';
import { wallDashboardSecretGuard } from './features/wall-dashboard/wall-dashboard-secret.guard';
import {
  getAccessGuard,
  getTitle,
  hasChangesCanDeactivate,
} from '@app/routing/route-helpers';
import { EAccessCode } from './app.enums';
import { selectDisruptionHasChanges } from './features/initial-events/disruption/state/disruption.selectors';
import { createInitEventHistoryRoute } from './features/initial-events/shared/init-event-history/init-event-history.routes';
import { provideState } from '@ngrx/store';
import { DisruptionReducer } from './features/initial-events/disruption/state/disruption.reducers';
import { provideEffects } from '@ngrx/effects';
import { DisruptionEffects } from './features/initial-events/disruption/state/disruption.effects';
import { DisruptionProtocolsEffects } from './features/initial-events/disruption/state/disruption-protocols.effects';
import { DisruptionPvsEffects } from './features/initial-events/disruption/state/disruption-pvs.effects';
import { DisruptionGridFiltersService } from './features/initial-events/disruption/disruption-grid-filters.service';
import { securityEventProtocolCanDeactivate } from './features/initial-events/security-events/security-event-protocol/security-event-protocol.guard';
import { SecurityEventsGridService } from './features/initial-events/security-events/security-events-grid.service';
import { SecurityEventsFeature } from './features/initial-events/security-events/state/security-events.reducer';
import { SecurityEventsEffects } from './features/initial-events/security-events/state/security-events.effects';

function chatListCardMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 0) {
    return { consumed: [] };
  }

  if (segments.length === 1) {
    return {
      consumed: segments,
      posParams: { reference: segments[0] },
    };
  }

  return null;
}

interface ChatMobileBackView {
  dismissTransientBackContext(): boolean;
  hasSelectedConversation(): boolean;
  confirmVoiceRecordingExit(): boolean | Promise<boolean>;
}

const preserveVoiceRecording: CanDeactivateFn<ChatMobileBackView> = (
  component,
) => component.confirmVoiceRecordingExit();

const preserveChatMobileBack: CanDeactivateFn<ChatMobileBackView> = (
  component,
  _currentRoute,
  _currentState,
  nextState,
) => {
  const router = inject(Router);
  const navigation = router.getCurrentNavigation();
  const isMobile =
    globalThis.matchMedia?.('(max-width: 48rem)').matches ??
    globalThis.innerWidth <= 768;

  if (!isMobile || navigation?.trigger !== 'popstate') {
    return true;
  }

  if (component.dismissTransientBackContext()) {
    return false;
  }

  const nextPath = nextState.url.split(/[?#]/, 1)[0];

  if (component.hasSelectedConversation() && nextPath !== '/chat-list') {
    return router.parseUrl('/chat-list');
  }

  return true;
};

export const routes: Routes = [
  {
    path: 'security-administrator',
    canActivate: [securityAdministratorGuard],
    loadComponent: () =>
      import('./features/auth/security-administrator/security-administrator.component').then(
        (c) => c.SecurityAdministratorComponent,
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import('./features/main/main.component').then((c) => c.MainComponent),
    canActivate: [authGuard],
  },
  {
    path: 'wall-dashboard',
    loadComponent: () =>
      import('./features/wall-dashboard/wall-dashboard.component').then(
        (c) => c.WallDashboardComponent,
      ),
    canActivate: [wallDashboardSecretGuard],
    canActivateChild: [wallDashboardSecretGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'metropolitan-summary-sheet',
      },
      {
        path: 'metropolitan-summary-sheet',
        data: {
          titleKey:
            'WALL_DASHBOARD.DASHBOARDS.METROPOLITAN_SUMMARY_SHEET.TITLE',
        },
        loadComponent: () =>
          import('./features/wall-dashboard/dashboards/metropolitan-summary-sheet/metropolitan-summary-sheet-dashboard.component').then(
            (c) => c.MetropolitanSummarySheetDashboardComponent,
          ),
      },
      {
        path: 'operational-analytics',
        data: {
          titleKey: 'WALL_DASHBOARD.DASHBOARDS.OPERATIONAL_ANALYTICS.TITLE',
        },
        loadComponent: () =>
          import('./features/wall-dashboard/dashboards/operational-analytics/operational-analytics-dashboard.component').then(
            (c) => c.OperationalAnalyticsDashboardComponent,
          ),
      },
    ],
  },
  {
    path: 'auth-code',
    loadComponent: () =>
      import('./features/auth/auth.component').then((c) => c.AuthComponent),
  },
  {
    path: 'admin',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  {
    path: 'monitoring',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/monitoring/monitoring.routes').then(
        (m) => m.MONITORING_ROUTES,
      ),
  },
  {
    path: 'assignments',
    title: () => getTitle('PROTOCOL_WORK.TITLE'),
    data: { titleKey: 'PROTOCOL_WORK.TITLE' },
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/protocol-work/protocol-work.component').then(
        (component) => component.ProtocolWorkComponent,
      ),
  },
  {
    path: 'informing',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/informing/informing.routes').then(
        (m) => m.INFORMING_ROUTES,
      ),
  },
  {
    path: 'nsi',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/nsi/nsi.routes').then((m) => m.NSI_ROUTES),
  },
  {
    path: 'oti',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/oti/oti.routes').then((m) => m.OTI_ROUTES),
  },
  {
    path: 'map',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/maps/map.routes').then((m) => m.MAP_ROUTES),
  },
  {
    path: 'dispatching',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/dispatching/dispatching.routes').then(
        (m) => m.DISPATCHING_ROUTES,
      ),
  },
  {
    path: 'chat-list',
    canActivate: [authGuard],
    data: { titleKey: 'CHAT_LIST.PAGE_BREADCRUMBS' },
    loadComponent: () =>
      import('./features/chat-list/chat-list.component').then(
        (c) => c.ChatListComponent,
      ),
    children: [
      {
        matcher: chatListCardMatcher,
        canDeactivate: [preserveVoiceRecording, preserveChatMobileBack],
        data: { titleKey: 'CHAT_LIST.PAGE_BREADCRUMBS' },
        loadComponent: () =>
          import('./features/chat-list/chat-list-card/chat-list-card.component').then(
            (c) => c.ChatListCardComponent,
          ),
      },
    ],
  },
  {
    path: 'events',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/initial-events/events.routes').then(
        (m) => m.EVENTS_ROUTES,
      ),
  },
  {
    path: 'event/disruption/:id',
    canActivate: [getAccessGuard(EAccessCode.c_InitiatingSituation)],
    canDeactivate: [hasChangesCanDeactivate(selectDisruptionHasChanges)],
    data: {
      titleKey: 'EVENT.DISRUPTION.PAGE_BREADCRUMBS',
    },
    title: () => getTitle('EVENT.PAGE_TITLE'),
    providers: [
      DisruptionGridFiltersService,
      provideState('disruption', DisruptionReducer),
      provideEffects(
        DisruptionEffects,
        DisruptionProtocolsEffects,
        DisruptionPvsEffects,
      ),
    ],
    loadComponent: () =>
      import('@app/features/initial-events/disruption/disruption-card/disruption-card.component').then(
        (c) => c.DisruptionCardComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('@app/features/initial-events/disruption/disruption-card/disruption-card-info/disruption-card-info.component').then(
            (c) => c.DisruptionCardInfoComponent,
          ),
      },
      createInitEventHistoryRoute({
        historyListTitleKey: 'EVENT.INIT_EVENT.HISTORY.TITLE',
        historyBackTitleKey: 'EVENT.DISRUPTION.CARD.HISTORY.BACK_TO_CARD',
        historyGridScope: 'disruption',
      }),
      {
        path: 'report',
        loadComponent: () =>
          import('@app/features/initial-events/disruption/disruption-card/disruption-card-report/disruption-card-report.component').then(
            (c) => c.DisruptionCardReportComponent,
          ),
      },
      {
        path: 'gantt-diagram',
        loadComponent: () =>
          import('@app/features/initial-events/disruption/disruption-card/disruption-card-gantt-diagram/disruption-card-gantt-diagram.component').then(
            (c) => c.DisruptionCardGanttDiagramComponent,
          ),
      },
    ],
  },
  {
    path: 'event/security/:id',
    canActivate: [getAccessGuard(EAccessCode.c_InitiatingSituation)],
    canDeactivate: [securityEventProtocolCanDeactivate],
    data: {
      titleKey: 'EVENT.SECURITY.PAGE_BREADCRUMBS',
    },
    providers: [
      SecurityEventsGridService,
      provideState(SecurityEventsFeature),
      provideEffects(SecurityEventsEffects),
    ],
    title: () => getTitle('EVENT.SECURITY.PAGE_TITLE'),
    loadComponent: () =>
      import('@app/features/initial-events/security-events/security-events-card/security-events-card.component').then(
        (c) => c.SecurityEventsCardComponent,
      ),
    children: [
      {
        path: 'gantt-diagram',
        loadComponent: () =>
          import('@app/features/initial-events/security-events/security-event-protocol/security-event-protocol-gantt.component').then(
            (c) => c.SecurityEventProtocolGanttComponent,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        canDeactivate: [securityEventProtocolCanDeactivate],
        loadComponent: () =>
          import('@app/features/initial-events/security-events/security-events-card/security-events-card-info/security-events-card-info.component').then(
            (c) => c.SecurityEventsCardInfoComponent,
          ),
      },
      createInitEventHistoryRoute({
        historyListTitleKey: 'EVENT.INIT_EVENT.HISTORY.TITLE',
        historyBackTitleKey: 'EVENT.SECURITY.CARD.HISTORY.BACK_TO_CARD',
        historyGridScope: 'security',
      }),
      {
        path: 'report',
        loadComponent: () =>
          import('@app/features/initial-events/security-events/security-events-card/security-events-report/security-events-report.component').then(
            (c) => c.SecurityEventsCardReportComponent,
          ),
      },
    ],
  },
  {
    path: 'orgstructure',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/orgstructure/orgstructure.routes').then(
        (m) => m.ORGSTRUCTURE_ROUTES,
      ),
  },
  {
    path: 'analytics',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/analytic/analytic.routes').then(
        (m) => m.ANALYTIC_ROUTES,
      ),
  },
  {
    path: 'internal-integration',
    canActivate: [authGuard],
    title: () => getTitle('INTERNAL_INTEGRATION.PAGE_TITLE'),
    data: {
      titleKey: 'INTERNAL_INTEGRATION.PAGE_BREADCRUMBS',
    },
    loadComponent: () =>
      import('./features/internal-integration/internal-integration.component').then(
        (c) => c.InternalIntegrationComponent,
      ),
  },
  {
    path: 'planned-events',
    loadChildren: () =>
      import('./features/planned-events/planned-events.routes').then(
        (m) => m.MAS_EVENTS_ROUTES,
      ),
  },
  {
    path: 'auditor/disruptions',
    pathMatch: 'full',
    redirectTo: 'events/disruptions',
  },
  {
    path: 'auditor/security',
    pathMatch: 'full',
    redirectTo: 'events/security',
  },
  { path: 'auditor', pathMatch: 'full', redirectTo: 'events/disruptions' },
  {
    path: 'page-not-found',
    title: () => getTitle('PAGE_NOT_FOUND.PAGE_TITLE'),
    component: PageNotFoundComponent,
  },
  {
    path: 'no-access',
    canActivate: [noAccessGuard],
    title: () => getTitle('NO_ACCESS.PAGE_TITLE'),
    component: NoAccessComponent,
  },
  { path: '**', redirectTo: 'page-not-found' },
];
